import { Component } from "react";
import { PermissionFullRow, PermissionRow, PolicyRow, RoleRow } from "namirasoft-access";
import { NSARouterMakerResult } from "namirasoft-account-react";
import { BaseColumnFormatter, BooleanFormatter, DateTimeFormatter, EmailFormatter, ForeColorFormatter, IBaseComponentProps, IDFormatter, NSButtonBlue, NSLoading, NSNoData, NSPanel, NSRow, NSSpace, NSSpaceSizeType, NSTable, NSTitle, StringFormatter, StringFormatterSizeType } from "namirasoft-site-react";
import { BaseMetaColumn, BaseMetaTable, EnvService, FilterItem, FilterItemOperator, IStorageLocal, NamingConvention, SortItem } from "namirasoft-core";
import { NamirasoftMap } from "namirasoft-site-map";
import { AccountRouterProps } from "../AccountRouterProps";

export interface ServiceUserAccessListProps extends IBaseComponentProps, NSARouterMakerResult, AccountRouterProps
{
    service_user_id: string | null;
    service_user_email?: string | null;
}

interface ServiceUserAccessListState
{
    loading: boolean;
    error: string | null;
    permissions: PermissionRow[];
    role_ids: string[];
    policy_ids: string[];
}

export default class ServiceUserAccessList extends Component<ServiceUserAccessListProps, ServiceUserAccessListState>
{
    constructor(props: ServiceUserAccessListProps)
    {
        super(props);
        this.state = {
            loading: false,
            error: null,
            permissions: [],
            role_ids: [],
            policy_ids: [],
        };
        this.reload = this.reload.bind(this);
    }

    override componentDidMount(): void
    {
        this.reload();
    }

    override componentDidUpdate(prevProps: ServiceUserAccessListProps): void
    {
        if (prevProps.service_user_id !== this.props.service_user_id
            || prevProps.service_user_email !== this.props.service_user_email)
            this.reload();
    }

    private async reload(): Promise<void>
    {
        if (!this.props.service_user_id)
        {
            this.setState({ loading: false, error: null, permissions: [], role_ids: [], policy_ids: [] });
            return;
        }

        this.setState({ loading: true, error: null });

        try
        {
            let access = this.props.servers.access;
            let namirasoft = new NamirasoftMap(this.props.account.token_manager, this.props.notifier.onError);
            let meta_permission = namirasoft.access.tables.permission;

            let table = new BaseMetaTable(null, meta_permission.name, meta_permission.text);
            let filters: FilterItem[] = [];
            if (this.props.service_user_email)
            {
                let email_column = new BaseMetaColumn(table, meta_permission.columns.to_email.name, meta_permission.columns.to_email.text, "", false);
                filters.push(new FilterItem(table, email_column, false, FilterItemOperator.all.equals, this.props.service_user_email));
            }
            else
            {
                let id_column = new BaseMetaColumn(table, meta_permission.columns.to_user_id.name, meta_permission.columns.to_user_id.text, "", false);
                filters.push(new FilterItem(table, id_column, false, FilterItemOperator.all.equals, this.props.service_user_id));
            }

            let { rows: permissions } = await access.permission.ListOutgoing(filters, null, null, [], null);

            let fulls: PermissionFullRow[] = await Promise.all(
                permissions.map(p => access.permission.GetOutgoing(p.id))
            );

            let role_set = new Set<string>();
            let policy_set = new Set<string>();
            for (let full of fulls)
            {
                for (let r of full.roles)
                    if (r)
                        role_set.add(r);
                for (let p of full.policies)
                    if (p)
                        policy_set.add(p);
            }

            this.setState({
                loading: false,
                error: null,
                permissions,
                role_ids: Array.from(role_set),
                policy_ids: Array.from(policy_set),
            });
        }
        catch (error: any)
        {
            this.props.notifier.onError(error);
            this.setState({ loading: false, error: error?.message ?? String(error) });
        }
    }

    private getAccessConsoleUrl(): string
    {
        return new EnvService("REACT_APP_BASE_URL_ACCESS_CONSOLE").getNullString() ?? "";
    }

    private openAccessConsole(path: string)
    {
        let base = this.getAccessConsoleUrl();
        if (!base)
            return;
        window.open(`${base}${path}`, "_blank");
    }

    override render()
    {
        if (!this.props.service_user_id)
            return null;

        let namirasoft = new NamirasoftMap(this.props.account.token_manager, this.props.notifier.onError);
        let meta_permission = namirasoft.access.tables.permission;
        let meta_role = namirasoft.access.tables.role;
        let meta_policy = namirasoft.access.tables.policy;
        let access = this.props.servers.access;

        let permission_columns = [
            BaseColumnFormatter.getColumn(meta_permission.columns.id, new IDFormatter(namirasoft, null)),
            BaseColumnFormatter.getColumn(meta_permission.columns.to_email, new EmailFormatter()),
            BaseColumnFormatter.getColumn(meta_permission.columns.to_first_name, new StringFormatter(StringFormatterSizeType.TwoWords)),
            BaseColumnFormatter.getColumn(meta_permission.columns.to_last_name, new StringFormatter(StringFormatterSizeType.TwoWords)),
            BaseColumnFormatter.getColumn(meta_permission.columns.from_email, new EmailFormatter()),
            BaseColumnFormatter.getColumn(meta_permission.columns.created_at, new DateTimeFormatter()),
            BaseColumnFormatter.getColumn(meta_permission.columns.updated_at, new DateTimeFormatter()),
        ];

        let role_columns = [
            BaseColumnFormatter.getColumn(meta_role.columns.id, new IDFormatter(namirasoft, null)),
            BaseColumnFormatter.getColumn(meta_role.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
            BaseColumnFormatter.getColumn(meta_role.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
            BaseColumnFormatter.getColumn(meta_role.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
            BaseColumnFormatter.getColumn(meta_role.columns.created_at, new DateTimeFormatter()),
            BaseColumnFormatter.getColumn(meta_role.columns.updated_at, new DateTimeFormatter()),
        ];

        let policy_columns = [
            BaseColumnFormatter.getColumn(meta_policy.columns.id, new IDFormatter(namirasoft, null)),
            BaseColumnFormatter.getColumn(meta_policy.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
            BaseColumnFormatter.getColumn(meta_policy.columns.product_id, new IDFormatter(namirasoft, new IStorageLocal())),
            BaseColumnFormatter.getColumn(meta_policy.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
            BaseColumnFormatter.getColumn(meta_policy.columns.entity, new StringFormatter(StringFormatterSizeType.TwoWords, NamingConvention.Pascal_Case_Space)),
            BaseColumnFormatter.getColumn(meta_policy.columns.actions, new StringFormatter(StringFormatterSizeType.FullName)),
            BaseColumnFormatter.getColumn(meta_policy.columns.resources, new StringFormatter(StringFormatterSizeType.Word)),
            BaseColumnFormatter.getColumn(meta_policy.columns.allow, new ForeColorFormatter(new BooleanFormatter("Allow", "Deny"), { true: "green", false: "red" })),
            BaseColumnFormatter.getColumn(meta_policy.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
            BaseColumnFormatter.getColumn(meta_policy.columns.created_at, new DateTimeFormatter()),
            BaseColumnFormatter.getColumn(meta_policy.columns.updated_at, new DateTimeFormatter()),
        ];

        let addAccessAction = (onFinished: () => void) =>
        {
            let base = this.getAccessConsoleUrl();
            if (base)
            {
                let path = "/permission/outgoing/new";
                if (this.props.service_user_email)
                    path += `?to_email=${encodeURIComponent(this.props.service_user_email)}`;
                window.open(`${base}${path}`, "_blank");
            }
            else
                this.props.notifier.onError(new Error("Access console URL is not configured."));
            onFinished();
        };

        return (
            <div id={this.props.id} className={this.props.classList?.join(" ")} style={{ width: "100%", ...this.props.style }}>
                <NSSpace size={NSSpaceSizeType.NORMAL} />
                <NSPanel style={{ width: "100%", maxWidth: "100%" }}>
                    <NSRow>
                        <NSTitle title="Access" />
                    </NSRow>
                    <NSRow>
                        <p style={{ color: "rgba(20, 27, 92, 1)", flexGrow: 1 }}>
                            Roles and policies granted to this service user via outgoing permissions.
                        </p>
                        <NSButtonBlue
                            title="Add Access"
                            onClick={{ action: addAccessAction, showLoading: false }}
                        />
                        <NSButtonBlue
                            title="Open in Access"
                            onClick={{
                                action: (onFinished) =>
                                {
                                    this.openAccessConsole("/permission/outgoing/list");
                                    onFinished();
                                },
                                showLoading: false
                            }}
                        />
                    </NSRow>

                    {
                        this.state.loading &&
                        <NSLoading title="Loading access ..." />
                    }

                    {
                        !this.state.loading && this.state.error &&
                        <NSNoData lable={`Failed to load access: ${this.state.error}`} />
                    }

                    {
                        !this.state.loading && !this.state.error &&
                        this.state.permissions.length === 0 &&
                        <NSNoData lable="No access has been granted to this service user yet." />
                    }
                </NSPanel>

                {
                    !this.state.loading && !this.state.error &&
                    this.state.permissions.length > 0 &&
                    <>
                        <NSSpace size={NSSpaceSizeType.NORMAL} />
                        <NSPanel style={{ width: "100%", maxWidth: "100%" }}>
                            <NSTitle title="Permissions" />
                            <NSTable<PermissionRow>
                                name={`ns-service-user-permissions-${this.props.service_user_id}`}
                                checkbox={false}
                                columns={permission_columns}
                                getRows={async () =>
                                {
                                    return { rows: this.state.permissions, count: this.state.permissions.length };
                                }}
                                getRowKey={e => e.value.id}
                                style={{ height: "auto" }}
                            />
                        </NSPanel>

                        <NSSpace size={NSSpaceSizeType.NORMAL} />
                        <NSPanel style={{ width: "100%", maxWidth: "100%" }}>
                            <NSTitle title="Roles" />
                            {
                                this.state.role_ids.length === 0
                                    ? <NSNoData lable="No roles granted." />
                                    : <NSTable<RoleRow>
                                        name={`ns-service-user-roles-${this.props.service_user_id}`}
                                        checkbox={false}
                                        columns={role_columns}
                                        getRows={async (page: number, size: number, sorts: SortItem[]) =>
                                        {
                                            let table = new BaseMetaTable(null, meta_role.name, meta_role.text);
                                            let column = new BaseMetaColumn(table, meta_role.columns.id.name, meta_role.columns.id.text, "", false);
                                            let filters = this.state.role_ids.map(id => new FilterItem(table, column, false, FilterItemOperator.all.equals, id));
                                            return await access.role.List(filters, page, size, sorts, null);
                                        }}
                                        getRowKey={e => e.value.id}
                                        style={{ height: "auto" }}
                                    />
                            }
                        </NSPanel>

                        <NSSpace size={NSSpaceSizeType.NORMAL} />
                        <NSPanel style={{ width: "100%", maxWidth: "100%" }}>
                            <NSTitle title="Policies" />
                            {
                                this.state.policy_ids.length === 0
                                    ? <NSNoData lable="No policies granted." />
                                    : <NSTable<PolicyRow>
                                        name={`ns-service-user-policies-${this.props.service_user_id}`}
                                        checkbox={false}
                                        columns={policy_columns}
                                        getRows={async (page: number, size: number, sorts: SortItem[]) =>
                                        {
                                            let table = new BaseMetaTable(null, meta_policy.name, meta_policy.text);
                                            let column = new BaseMetaColumn(table, meta_policy.columns.id.name, meta_policy.columns.id.text, "", false);
                                            let filters = this.state.policy_ids.map(id => new FilterItem(table, column, false, FilterItemOperator.all.equals, id));
                                            return await access.policy.List(filters, page, size, sorts, null);
                                        }}
                                        getRowKey={e => e.value.id}
                                        style={{ height: "auto" }}
                                    />
                            }
                        </NSPanel>
                    </>
                }
            </div>
        );
    }
}